import { CanvasContext } from "client/contexts/ResizeEditor/Canvas";
import { gtag } from "client/utils/adsense/gtag";
import { calculateCanvasImageDimensions } from "client/utils/calculateCanvasImageDimensions";
import { downloadImageInFrontendTools } from "client/utils/downloadImageInFrontendTools";
import { formatBytes } from "client/utils/formatBytes";
import { getUrlExtension } from "client/utils/getUrlExtension";
import { loadImage } from "client/utils/imageEdit/loadImage";
import { loadSrcToImage } from "client/utils/loadSrcToImage";
import { translateFunc } from "client/utils/translateFunc";
import { DEFAULT_TOAST_SHOW_TIME } from "components/common/Toast/ToastRenderer";
import { useToast } from "components/common/Toast/useToast";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";
import { setImage } from "store/features/crop/actions";
import { cropSelector } from "store/features/crop/selectors/cropSelector";
import { CropState } from "store/features/crop/types";
import { useLoadFromParentImage } from "store/features/resizeEditor/hooks/useLoadFromParentImage";
import { useAppDispatch, useAppSelector } from "store/store";
import { Size } from "store/types/previewImage";
import { useImageCropperCropSettings } from "../EditSettings/CropSettings/useImageCropperCropSettings";

export const useCropPreview = () => {
    const { image, upload } = useAppSelector(cropSelector, equalityFn);
    const { parentImageRef, cropRef, imageLoaded, setImageLoaded, initialImageRef } = useContext(CanvasContext);
    const previewContainerRef = useRef<HTMLDivElement | null>(null);
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [downloaded, setDownloaded] = useState<Size>("0 KB");
    const startedLoadingRef = useRef(false);
    const router = useRouter();
    const toast = useToast();
    useLoadFromParentImage(cropRef);
    const errorFromEditorText = translateFunc(`common:corrupt-image-toast-test`);
    useImageCropperCropSettings();

    const handleLoadImageAnalytics = () => {
        ///event
        gtag("event", "Editor Loaded", {
            event_category: "Image Cropper",
            event_label: "Editor Page",
        });
    };

    const handleLoadImage = async () => {
        //  imageLoaded check is to prevent unnecessary loading
        const parentImage = parentImageRef?.current;
        const initialImage = initialImageRef?.current;
        const cropImage = cropRef?.current;
        const previewContainer = previewContainerRef.current;
        if (
            !parentImage ||
            !previewContainer ||
            imageLoaded ||
            !image ||
            startedLoadingRef.current ||
            !initialImage ||
            !cropImage
        )
            return;
        startedLoadingRef.current = true;
        handleLoadImageAnalytics();

        const { src } = await downloadImageInFrontendTools(
            upload,
            image.src,
            0,
            (_progress, downloaded) => setDownloaded(formatBytes(downloaded)),
            {},
            image.ext,
        );
        const { image: img, error } = await loadImage(src);
        if (error) {
            toast.toast({
                closeable: true,
                position: "top-right",
                status: "error",
                text: errorFromEditorText,
                time: DEFAULT_TOAST_SHOW_TIME,
            });
            setTimeout(() => {
                setIsLoading(false);
                router.back();
            }, 500);
        } else {
            await loadSrcToImage(src, initialImage);
            const { width, height } = setInitialImage(
                img,
                previewContainer.getBoundingClientRect().width,
                previewContainer.getBoundingClientRect().height,
            );
            parentImage.width = width;
            parentImage.height = height;

            await loadSrcToImage(src, parentImage)
                .then((_) => {
                    initialImage.width = width;
                    setImageLoaded(true);
                    setIsLoading(false);
                })
                .catch((_) => {
                    setImageLoaded(false);
                    toast.toast({
                        closeable: true,
                        position: "top-right",
                        status: "error",
                        text: errorFromEditorText,
                        time: DEFAULT_TOAST_SHOW_TIME,
                    });
                    setTimeout(() => {
                        setIsLoading(false);
                        router.back();
                    }, 500);
                });
        }
    };

    const setInitialImage = (
        img: HTMLImageElement,
        containerWidth: number,
        containerHeight: number,
    ): { width: number; height: number } => {
        const { width, height } = calculateCanvasImageDimensions(
            img.width,
            img.height,
            containerWidth,
            containerHeight,
            false,
        );

        dispatch(
            setImage({
                src: image?.src ?? "",
                height: height,
                width: width,
                initialHeight: height,
                initialWidth: width,
                originalWidth: img.width,
                originalHeight: img.height,
                ratio: img.width / img.height,
                title: image?.title ?? "",
                containerWidth,
                containerHeight,
                ext: image?.ext ?? getUrlExtension(image?.src ?? ""),
            }),
        );

        return { width, height };
    };

    useEffect(() => {
        !imageLoaded && handleLoadImage();
    }, [parentImageRef?.current, image]);

    return {
        image,
        cropRef,
        previewContainerRef,
        parentImageRef,
        isLoading,
        initialImageRef,
        downloaded,
    };
};

const equalityFn = (left: CropState, right: CropState) => {
    if (JSON.stringify(left.image) !== JSON.stringify(right.image)) return false;

    return true;
};
